import { useMemo } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, Receipt } from "lucide-react";

import { useMyInvoices } from "@/features/invoices/model/useInvoices";
import type { Invoice, InvoiceStatus } from "@/features/invoices/types";
import { Badge, type BadgeProps } from "@/shared/ui";

const STATUS_VARIANT: Record<InvoiceStatus, BadgeProps["variant"]> = {
  GENERATED: "warning",
  SENT: "info",
  PAID: "success",
};

const STATUS_LABELS: Record<InvoiceStatus, string> = {
  GENERATED: "Draft",
  SENT: "Outstanding",
  PAID: "Paid",
};

function fmtPeriod(start: string, end: string): string {
  const s = new Date(start);
  const e = new Date(end);
  const sameMonth = s.getMonth() === e.getMonth() && s.getFullYear() === e.getFullYear();
  const sFmt = s.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: sameMonth ? undefined : "numeric",
  });
  const eFmt = e.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
  return `${sFmt} – ${eFmt}`;
}

function fmtShort(iso: string | null): string {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function shortInvoiceId(id: string): string {
  return id.slice(0, 8).toUpperCase();
}

export function VenueInvoicesPage() {
  const { data, isLoading, error } = useMyInvoices();

  const totals = useMemo(() => {
    const list = data ?? [];
    const outstanding = list
      .filter((i) => i.status === "SENT")
      .reduce((acc, i) => acc + parseFloat(i.total_charge), 0);
    const paidYTD = list
      .filter((i) => i.status === "PAID")
      .reduce((acc, i) => acc + parseFloat(i.total_charge), 0);
    return { outstanding, paidYTD, count: list.length };
  }, [data]);

  return (
    <div className="space-y-4">
      <header className="flex items-start gap-3">
        <div className="rounded-md bg-primary-light p-2 text-primary">
          <Receipt className="h-5 w-5" />
        </div>
        <div>
          <h2 className="text-lg font-semibold tracking-display-tight text-copy">Invoices</h2>
          <p className="mt-0.5 text-sm text-copy-muted">
            Weekly billing for completed shifts. Pay via Interac e-Transfer to{" "}
            <span className="font-medium text-copy">billing@oneshift.ca</span> using the invoice
            reference. Drafts under review by OneShift aren't shown here yet — you'll get an email
            when an invoice is finalized.
          </p>
        </div>
      </header>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <SummaryCard
          label="Outstanding"
          amount={totals.outstanding}
          tone={totals.outstanding > 0 ? "warning" : "neutral"}
        />
        <SummaryCard label="Paid to date" amount={totals.paidYTD} tone="neutral" />
      </div>

      {isLoading && <div className="text-sm text-copy-muted">Loading…</div>}
      {error && <div className="text-sm text-destructive">Could not load invoices.</div>}

      {!isLoading && !error && data && data.length === 0 && (
        <div className="rounded-lg border border-border bg-surface p-10 text-center shadow-card">
          <p className="text-sm font-medium text-copy">No invoices yet</p>
          <p className="mt-1 text-sm text-copy-muted">
            Invoices are generated weekly on Mondays for the prior week's completed shifts.
          </p>
        </div>
      )}

      {data && data.length > 0 && (
        <div className="overflow-hidden rounded-lg border border-border bg-surface shadow-card">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[560px] text-sm">
              <thead className="border-b border-border bg-surface-sunken text-left text-xs uppercase tracking-wider text-copy-muted">
                <tr>
                  <th className="px-4 py-3 font-medium">Invoice</th>
                  <th className="px-4 py-3 font-medium">Period</th>
                  <th className="px-4 py-3 text-right font-medium">Total</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium" aria-label="Open" />
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {data.map((inv) => (
                  <InvoiceRow key={inv.id} invoice={inv} />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function InvoiceRow({ invoice }: { invoice: Invoice }) {
  return (
    <tr className="transition-colors hover:bg-surface-sunken/40">
      <td className="px-4 py-3">
        <Link
          to={`/venue/invoices/${invoice.id}`}
          className="font-medium text-copy hover:text-primary"
        >
          INV-{shortInvoiceId(invoice.id)}
        </Link>
      </td>
      <td className="px-4 py-3 text-xs text-copy-muted">
        {fmtPeriod(invoice.period_start, invoice.period_end)}
      </td>
      <td className="px-4 py-3 text-right font-semibold tabular-nums text-copy">
        ${invoice.total_charge}
      </td>
      <td className="px-4 py-3">
        <Badge variant={STATUS_VARIANT[invoice.status]} dot>
          {STATUS_LABELS[invoice.status]}
        </Badge>
        {invoice.status === "SENT" && invoice.sent_at && (
          <div className="mt-1 text-[10px] text-copy-subtle">
            Sent {fmtShort(invoice.sent_at)}
          </div>
        )}
        {invoice.status === "PAID" && invoice.paid_at && (
          <div className="mt-1 text-[10px] text-copy-subtle">
            Paid {fmtShort(invoice.paid_at)}
          </div>
        )}
      </td>
      <td className="px-4 py-3 text-right">
        <Link
          to={`/venue/invoices/${invoice.id}`}
          className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
        >
          View <ChevronRight className="h-3.5 w-3.5" />
        </Link>
      </td>
    </tr>
  );
}

function SummaryCard({
  label,
  amount,
  tone,
}: {
  label: string;
  amount: number;
  tone: "warning" | "neutral";
}) {
  const valueClass = tone === "warning" ? "text-warning-foreground" : "text-copy";
  return (
    <div className="rounded-lg border border-border bg-surface p-4 shadow-card">
      <p className="text-xs uppercase tracking-wider text-copy-subtle">{label}</p>
      <p className={`mt-1 text-2xl font-semibold tabular-nums ${valueClass}`}>
        ${amount.toFixed(2)}
      </p>
    </div>
  );
}
